import * as THREE from 'three'

import { ENV } from '../config/constants.js'
import { cirrusField, cumulusField, lowestBaseFt } from '../world/cloudField.js'

/**
 * A scattered cumulus layer, and a thin cirrus sheet above it.
 *
 * The sky was the emptiest thing in the simulator: a smooth blue gradient with
 * nothing in it, which reads as a rendering placeholder rather than as weather.
 * It also robs the pattern of any sense of motion — turn ninety degrees under
 * a featureless dome and nothing changes.
 *
 * THE CEILING IS NOT A STYLE CHOICE
 * CASE 1 is defined by the weather: it requires a ceiling of at least 3,000 ft
 * and five miles of visibility. Below that you are flying CASE 2 or CASE 3,
 * which is a different recovery with different procedures — a marshal stack, a
 * timed approach, a controlled descent. So the cloud bases here sit at 3,500 ft
 * and the tops well above, which is what makes this a CASE 1 day rather than
 * decoration that happens to contradict the trainer's own subject.
 *
 * Built from camera-facing sprites rather than volumetrics. At a distance of
 * miles a cumulus is a soft lit blob, and eighty sprites cost nothing next to
 * one raymarched cloud that would look no better at this range.
 */

/** A soft, slightly lumpy blob with a lit top and a shaded base. */
function puffTexture(size = 256) {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  const g = c.getContext('2d')

  // Several overlapping radial gradients make a lumpier silhouette than one.
  const lobes = [
    [0.5, 0.56, 0.42],
    [0.32, 0.6, 0.28],
    [0.68, 0.6, 0.3],
    [0.44, 0.44, 0.26],
    [0.6, 0.46, 0.24],
  ]
  for (const [cx, cy, r] of lobes) {
    const grad = g.createRadialGradient(
      cx * size,
      cy * size,
      0,
      cx * size,
      cy * size,
      r * size,
    )
    // Lit from above: the top of each lobe is bright, the underside grey.
    grad.addColorStop(0, 'rgba(255,255,255,0.62)')
    grad.addColorStop(0.45, 'rgba(246,249,252,0.34)')
    grad.addColorStop(0.78, 'rgba(196,209,221,0.12)')
    grad.addColorStop(1, 'rgba(180,196,210,0)')
    g.fillStyle = grad
    g.fillRect(0, 0, size, size)
  }

  // Darken the bottom third: a cumulus has a flat, shaded base, and that base
  // is the single cue that says "cloud" rather than "smudge".
  const base = g.createLinearGradient(0, size * 0.58, 0, size)
  base.addColorStop(0, 'rgba(150,166,182,0)')
  base.addColorStop(1, 'rgba(126,143,160,0.20)')
  g.globalCompositeOperation = 'source-atop'
  g.fillStyle = base
  g.fillRect(0, 0, size, size)
  g.globalCompositeOperation = 'source-over'

  const tex = new THREE.CanvasTexture(c)
  // Every other canvas texture in this project sets it, and without it the
  // sRGB greys painted above are consumed as linear — about twice as bright.
  // The shaded flat base, which is the single cue that says "cloud" rather
  // than "smudge", washes out to white.
  tex.colorSpace = THREE.SRGBColorSpace
  tex.needsUpdate = true
  return tex
}

export class Clouds {
  /**
   * @param {THREE.Color} hazeColor so the far clouds fade into the same
   *        horizon the sea and the sky fade into
   */
  constructor(hazeColor, quality = {}) {
    this.group = new THREE.Group()
    this.group.name = 'Clouds'

    this.texture = puffTexture()
    this.material = new THREE.SpriteMaterial({
      map: this.texture,
      transparent: true,
      depthWrite: false,
      // Fog on, so a cloud twenty miles out is the colour of the horizon
      // rather than a bright white blob hanging in the haze.
      fog: true,
      opacity: 0.95,
    })
    this.material.color = new THREE.Color(0xffffff)

    // ---- cumulus -------------------------------------------------------
    // Cumulus only in this list. The cirrus sheet is seven kilometres up and is
    // not a "cloud base" in any sense the CASE 1 minimum cares about, so
    // keeping the two together meant `lowestBaseFt()` had to tell them apart by
    // material identity — a check that silently starts passing everything the
    // moment anyone gives the cirrus its own material, which is what happened.
    this.cumulus = cumulusField(quality.cloudClumps)
    for (const p of this.cumulus) {
      const s = new THREE.Sprite(this.material)
      s.scale.set(p.w, p.h, 1)
      s.position.set(p.x, p.y, p.z)
      this.group.add(s)
    }

    // ---- high cirrus ----------------------------------------------------
    // A thin sheet a long way up. Barely visible, but it stops the top of the
    // sky being a flat wash of colour when you look up in the break.
    this.cirrusTexture = puffTexture(128)
    this.cirrusMaterial = new THREE.SpriteMaterial({
      map: this.cirrusTexture,
      transparent: true,
      depthWrite: false,
      opacity: 0.3,
      // Fog OFF. The fog ends at 33 km and the cirrus sits at 7 km up and up
      // to 39 km out — every sprite was 100% fogged, which is a lot of work to
      // draw twenty-two patches of exactly the haze colour. High cloud is
      // above the haze layer anyway, which is why you can see it on a hazy day.
      fog: false,
    })
    for (const p of cirrusField()) {
      const s = new THREE.Sprite(this.cirrusMaterial)
      s.position.set(p.x, p.y, p.z)
      s.scale.set(p.w, p.h, 1)
      this.group.add(s)
    }

    void hazeColor
  }

  /**
   * Keep the field centred on the aircraft.
   *
   * The pattern covers about four miles; the cloud field is twenty-six across.
   * Without recentring you would fly out from under it in a couple of passes
   * and be left with the empty sky this exists to fix. Recentring in whole
   * spacings would pop; recentring the whole group is invisible because the
   * clouds are far enough away that the parallax over one pattern is small.
   */
  update(cameraPosition) {
    this.group.position.x = cameraPosition.x
    this.group.position.z = cameraPosition.z
  }

  /** Lowest visible cloud base, feet. Used by the test to hold this to CASE 1. */
  lowestBaseFt() {
    return lowestBaseFt(this.cumulus)
  }

  dispose() {
    this.material.dispose()
    this.cirrusMaterial.dispose()
    this.texture.dispose()
    this.cirrusTexture.dispose()
  }
}

void ENV
